import {
	data,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import { href as iconsHref, Icon } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import {
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
// Inter, self-hosted. NOT the Google Fonts CDN — entry.server.tsx sets
// `font-src: 'self'`, so a fonts.gstatic.com link is blocked by our own CSP.
// index.css declares 7 unicode-range subsets, so a browser fetches latin only.
import interStyleSheetUrl from '@fontsource-variable/inter/index.css?url'
import interLatinWoff2Url from '@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { listOwnedBillingAccounts } from './utils/billing-account.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { getLastBaId, getLastBookId, setBookIdHeader } from './utils/view-cookie.server.ts'
import { prisma, withBypassRLS } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser } from './utils/user.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		// Preloaded because the @font-face sits inside a stylesheet: without this
		// the browser can't discover the woff2 until that CSS has parsed, which
		// is a second round-trip of fallback text. `crossOrigin` is required on
		// font preloads even same-origin, or the preload is fetched twice.
		{
			rel: 'preload',
			href: interLatinWoff2Url,
			as: 'font',
			type: 'font/woff2',
			crossOrigin: 'anonymous',
		} as const,
		{ rel: 'stylesheet', href: interStyleSheetUrl },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'LedgerAI' : 'Error | LedgerAI' },
		{ name: 'description', content: `Your own captain's log` },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							email: true,
							name: true,
							username: true,
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}

	// 3-scope chrome needs: accessible BAs + accessible books (with BA),
	// + a "current" hint for each. Resolution order: URL → cookie → first.
	// All resolved server-side so chrome renders SSR with no client state.
	const accessibleBillingAccounts = userId
		? await listOwnedBillingAccounts(userId)
		: []
	const accessibleBooks = userId
		? await withBypassRLS(async (db) => {
				const owned = await db.book.findMany({
					where: { ownerId: userId },
					select: { id: true, name: true, billingAccountId: true },
					orderBy: { name: 'asc' },
				})
				const grants = await db.accessGrant.findMany({
					where: {
						userId,
						scope: { scopeType: 'book' },
						OR: [{ expiresAt: null }, { expiresAt: { gt: new Date() } }],
					},
					select: { scopeId: true },
				})
				const ownedIds = new Set(owned.map((b) => b.id))
				const sharedIds = grants
					.map((g) => g.scopeId)
					.filter((id) => id && !ownedIds.has(id))
				const shared = sharedIds.length
					? await db.book.findMany({
							where: { id: { in: sharedIds } },
							select: { id: true, name: true, billingAccountId: true },
							orderBy: { name: 'asc' },
						})
					: []
				return [...owned, ...shared]
			})
		: []

	// Gallery-owned public books. Sourced separately so the scope-dropdown
	// can distinguish them visually (globe icon vs book icon) and search
	// across both sets. Bypasses RLS because the boundary here is
	// `ownerId = 'gallery'` — the same rows /gallery serves publicly, so
	// they're intentionally cross-tenant.
	const galleryBooks = await withBypassRLS((db) =>
		db.book.findMany({
			where: { ownerId: 'gallery' },
			select: { id: true, name: true, billingAccountId: true },
			orderBy: { createdAt: 'desc' },
		}),
	)

	const url = new URL(request.url)
	// Scope invariant: deepest scope in the URL wins. Sidebar tiers
	// mirror this — Global needs nothing, BA needs a BA, Book needs both
	// (and Book implies its BA). So when a book is in scope, its BA
	// overrides any cookie/`/ba/$id` hint — otherwise chrome can display
	// a BA that disagrees with the body's book.
	const urlBookMatch =
		url.pathname.match(/^\/b\/([^/]+)/) ??
		url.pathname.match(/^\/books\/([^/]+)/)
	const urlBookId =
		urlBookMatch?.[1] ?? url.searchParams.get('book') ?? null
	const bookInScope =
		urlBookId
			? accessibleBooks.find((b) => b.id === urlBookId) ?? null
			: null

	const urlBaMatch = url.pathname.match(/^\/ba\/([^/]+)/)
	const urlBaId = urlBaMatch?.[1] ?? null
	const cookieBaId = await getLastBaId(request)
	const currentBaId =
		// Deepest scope wins: book's BA when a book is in URL.
		(bookInScope?.billingAccountId ?? null) ??
		(urlBaId && accessibleBillingAccounts.some((b) => b.id === urlBaId)
			? urlBaId
			: null) ??
		(cookieBaId && accessibleBillingAccounts.some((b) => b.id === cookieBaId)
			? cookieBaId
			: null) ??
		accessibleBillingAccounts[0]?.id ??
		null

	const cookieBookId = await getLastBookId(request)
	// Book hint defaults to first book in current BA if available.
	const booksInCurrentBa = currentBaId
		? accessibleBooks.filter((b) => b.billingAccountId === currentBaId)
		: accessibleBooks
	const currentBookId =
		(bookInScope?.id ?? null) ??
		(cookieBookId && booksInCurrentBa.some((b) => b.id === cookieBookId)
			? cookieBookId
			: null) ??
		// Gallery-owned books aren't in accessibleBooks (user has no direct
		// grant), but /books/:publicId sets the cookie and /report/* reads
		// from it — so the viewed book is a public one. Trust the cookie
		// here so the scope-dropdown label matches what's on screen. The
		// dropdown's list remains sourced from accessibleBooks (gallery
		// books filtered out) — see scope-dropdown.tsx.
		cookieBookId ??
		booksInCurrentBa[0]?.id ??
		null

	// Resolve the display name for currentBookId. accessibleBooks covers
	// owned + shared; if it's a gallery/public book (cookie-only path),
	// look it up privileged so the selector can render its name. Cheap:
	// one row by primary key, only when we don't already have it.
	let currentBookMeta: { id: string; name: string } | null = null
	if (currentBookId) {
		const inList = accessibleBooks.find((b) => b.id === currentBookId)
		if (inList) {
			currentBookMeta = { id: inList.id, name: inList.name }
		} else {
			const b = await withBypassRLS((db) =>
				db.book.findUnique({
					where: { id: currentBookId },
					select: { id: true, name: true },
				}),
			)
			currentBookMeta = b
		}
	}

	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	// 2026-08-28 — sync the book cookie whenever a URL-scoped book route
	// resolves to a different bookId than the cookie holds. Fixes the
	// nav-mismatch bug: visiting /books/canary-v1 doesn't update the
	// cookie, so clicking a top-nav link to /report/pnl (cookie-scoped)
	// falls back to whichever stale book the cookie last held (paul).
	// Now the URL wins and the cookie follows — cookie-scoped surfaces
	// stay in sync with URL-scoped navigation.
	//
	// 2026-09-02 — also seed the cookie whenever currentBookId is
	// derived from ANY fallback (accessibleBooks[0], first-in-current-BA)
	// without a matching cookie. Otherwise the nav derives its enabled
	// state from currentBookId (seen as truthy) but a click into
	// /report/* hits requireBookAccessFromCookie which reads the cookie
	// strictly, finds nothing, and redirects to /books — the nav item
	// LOOKS clickable but doesn't work. Symmetric: root derives
	// currentBookId → root persists it → cookie-strict readers agree.
	const bookCookieHeaders: Record<string, string> = {}
	if (currentBookId && currentBookId !== cookieBookId) {
		bookCookieHeaders['Set-Cookie'] = await setBookIdHeader(request, currentBookId)
	}

	return data(
		{
			user,
			accessibleBillingAccounts,
			currentBaId,
			accessibleBooks,
			galleryBooks,
			currentBookId,
			currentBookMeta,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				bookCookieHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = typeof ENV !== 'undefined' ? ENV.ALLOW_INDEXING !== 'false' : true
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

// Chrome ownership is now delegated entirely to per-section _layout.tsx:
//   - _site+/_layout.tsx  -> SiteTopNav + SiteFooter
//   - _auth+/_layout.tsx       -> compact wordmark-only header
//   - books+/_layout.tsx, report+/_layout.tsx, etc.
// The old allow-list (MARKETING_ROUTES / AUTH_PREFIXES / ownsChrome) was
// removed 2026-08-21 because it was an exact-match set that silently
// double-navved every nested route (e.g. /gallery under _site+/gallery+/
// was not in the set, so both the marketing chrome AND the legacy root chrome
// rendered). Along with it went the fallback legacy Logo header + BottomNav
// footer, which the icon.com-style rebrand superseded. Unauthed routes with
// no _layout.tsx render bare; authed routes take the sidebar shell below.

function App() {
	const data = useLoaderData<typeof loader>()
	const theme = useTheme()
	useToast(data.toast)

	// Every route owns its chrome via its section _layout.tsx:
	//   - _site+/_layout.tsx    → SiteTopNav + SiteFooter (marketing)
	//   - _auth+/_layout.tsx    → compact wordmark shell (auth flows)
	//   - _app+/_layout.tsx     → <AppShell> (sidebar + inner header + baked padding)
	//   - books+/_layout.tsx    → <AppShell> today; step 2 of 848 upgrades to
	//                             viewer-conditional AppShell vs BookReaderShell
	//   - top-level routes (connect.tsx, api+/, resources+/, $.tsx) render bare
	// root.tsx renders only the outlet + global toaster/progress. No auth-based
	// conditional here — the previous `if (user)` branch was replaced by
	// _app+/_layout.tsx in issue 848 step 1.
	return (
		<>
			<Outlet />
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</>
	)
}

function Logo() {
	return (
		<Link to="/" className="group leading-snug">
			<span className="font-bold transition group-hover:translate-x-1">
				LedgerAI
			</span>
		</Link>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// Reusable copyable error box component
function CopyableErrorBox({ title, content }: { title: string; content: string }) {
	return (
		<div className="mt-4 w-full max-w-2xl">
			<div className="flex items-center justify-between rounded-t-lg bg-muted px-4 py-2">
				<span className="text-xs font-medium text-muted-foreground">{title}</span>
				<Button
					variant="ghost"
					size="sm"
					className="h-7 text-xs"
					onClick={() => {
						void navigator.clipboard.writeText(content)
					}}
				>
					<Icon name="copy" className="mr-1 h-3 w-3" />
					Copy
				</Button>
			</div>
			<pre className="max-h-64 overflow-auto rounded-b-lg bg-muted/50 p-4 text-xs whitespace-pre-wrap break-all">
				{content}
			</pre>
		</div>
	)
}

// Root error boundary with navigation back to home
export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			defaultStatusHandler={({ error }) => {
				const errorContent = typeof error.data === 'string' 
					? error.data 
					: JSON.stringify(error.data, null, 2)
				
				return (
					<div className="flex flex-col items-center gap-6">
						<div className="flex h-24 w-24 items-center justify-center rounded-full bg-destructive/10">
							<span className="text-4xl">⚠️</span>
						</div>
						<div className="text-center">
							<h1 className="text-h1 mb-2">
								{error.status} Error
							</h1>
							<p className="text-muted-foreground text-lg">
								{error.statusText || 'Something went wrong'}
							</p>
						</div>
						<div className="flex gap-4">
							<Button asChild variant="default">
								<Link to="/">Go Home</Link>
							</Button>
							<Button
								variant="outline"
								onClick={() => window.location.reload()}
							>
								Try Again
							</Button>
						</div>
						{errorContent && (
							<CopyableErrorBox title="Error Details" content={errorContent} />
						)}
					</div>
				)
			}}
			unexpectedErrorHandler={(error) => {
				const errorMessage = error instanceof Error ? error.message : String(error)
				const errorStack = error instanceof Error ? error.stack : null
				
				return (
					<div className="flex flex-col items-center gap-6">
						<div className="flex h-24 w-24 items-center justify-center rounded-full bg-destructive/10">
							<span className="text-4xl">💥</span>
						</div>
						<div className="text-center">
							<h1 className="text-h1 mb-2">Unexpected Error</h1>
							<p className="text-muted-foreground text-lg max-w-md">
								An unexpected error occurred
							</p>
						</div>
						<div className="flex gap-4">
							<Button asChild variant="default">
								<Link to="/">Go Home</Link>
							</Button>
							<Button
								variant="outline"
								onClick={() => window.location.reload()}
							>
								Try Again
							</Button>
						</div>
						<CopyableErrorBox title="Error Message" content={errorMessage} />
						{errorStack && (
							<CopyableErrorBox title="Stack Trace" content={errorStack} />
						)}
					</div>
				)
			}}
		/>
	)
}
